import Papa from "papaparse"
import { db } from "../firebase"
import {
	addDoc,
	collection,
	getDocs,
	query,
	where,
	Timestamp,
} from "firebase/firestore"
import axios from "axios"
import config from "../config.js"
import { sendJobToPPW } from "./SendJobToPPW.js"

// Required fields for job request validation
export const REQUIRED_FIELDS = [
	"Address",
	"City",
	"State",
	"Zip",
	"Is Deal Under Contract",
	"Is Electric On?",
	"Is Water On?",
	"Is Gas On?",
	"Is Property Occupied?",
	"Service Type",
	"Preferred Service Dates",
	"Job Request Details",
]

// Fields that are only required when "Is Deal Under Contract" is true
export const CONTRACT_CONDITIONAL_FIELDS = [
	"Inspection Period Date",
	"Closing Date",
]

export const OCCUPANCY_CONDITIONAL_FIELDS = [
	"Current Resident Name",
	"Current Resident Phone Number",
	"Current Resident Email",
]

/**
 * Validates if a row has all required fields, with conditional validation
 * @param {Object} row - The row data to validate
 * @returns {Object} Validation result with valid flag and missing fields
 */
export const validateRow = (row) => {
	// Determine which fields are required based on conditions
	const isDealUnderContract = row["Is Deal Under Contract"] === true
	const isPropertyOccupied = row["Is Property Occupied?"] === true

	// Start with the base required fields
	let fieldsToCheck = [...REQUIRED_FIELDS]

	// Add conditional fields if the deal is under contract
	if (isDealUnderContract) {
		fieldsToCheck = [...fieldsToCheck, ...CONTRACT_CONDITIONAL_FIELDS]
	}

	if (isPropertyOccupied) {
		fieldsToCheck = [...fieldsToCheck, ...OCCUPANCY_CONDITIONAL_FIELDS]
	}

	// Check for missing required fields, properly handling boolean values
	const missingFields = fieldsToCheck.filter((field) => {
		// The field value
		const value = row[field]

		// Special handling for boolean values
		if (typeof value === "boolean") {
			return false // Booleans (true OR false) are considered valid values
		}

		// Handle null, undefined, or empty strings
		return value === null || value === undefined || String(value).trim() === ""
	})

	// Return the validation result
	if (missingFields.length > 0) {
		return {
			valid: false,
			missingFields,
			conditionalValidation: isDealUnderContract
				? "Contract fields required"
				: "Standard validation",
		}
	}

	return { valid: true }
}

/**
 * @param {string} value - The string value to convert
 * @param {boolean} defaultValue - Optional default value if the input is undefined/null
 * @returns {boolean} The converted boolean value
 */
export const stringToBoolean = (value, defaultValue = false) => {
	// Handle undefined/null values
	if (value === undefined || value === null) {
		return defaultValue
	}

	// Convert to string in case we get a non-string input
	const stringValue = String(value).trim().toLowerCase()

	// Check for truthy values
	if (
		stringValue === "yes" ||
		stringValue === "true" ||
		stringValue === "y" ||
		stringValue === "1" ||
		stringValue === "on"
	) {
		return true
	}

	// Check for falsy values
	if (
		stringValue === "no" ||
		stringValue === "false" ||
		stringValue === "n" ||
		stringValue === "0" ||
		stringValue === "off"
	) {
		return false
	}

	// Default case
	return defaultValue
}

/**
 * Process a row of CSV data and convert Yes/No fields to booleans
 *
 * @param {Object} row - The CSV row data
 * @returns {Object} Processed row with boolean values
 */
export const processCsvRow = (row) => {
	// Create a copy of the row to avoid mutating the original
	const processedRow = { ...row }

	// Fields that should be converted to boolean
	const booleanFields = [
		"Is Deal Under Contract",
		"Is Electric On?",
		"Is Water On?",
		"Is Gas On?",
		"Is Property Occupied?",
	]

	Object.keys(processedRow).forEach((field) => {
		if (
			processedRow[field] === undefined ||
			processedRow[field] === null ||
			String(processedRow[field]).trim() === ""
		) {
			processedRow[field] = null
		}
	})
	// Convert the specified fields to boolean
	booleanFields.forEach((field) => {
		if (field in processedRow && processedRow[field] !== null) {
			processedRow[field] = stringToBoolean(processedRow[field])
		}
	})
	return processedRow
}

/**
 * Formats address components into a single address string
 * @param {string} street - Street address
 * @param {string} city - City name
 * @param {string} state - State abbreviation
 * @param {string|number} zip - ZIP code
 * @returns {string} Formatted address string
 */
export const formatAddress = (street, city, state, zip) => {
	// Handle empty or undefined values
	const streetStr = street || ""
	const cityStr = city || ""
	const stateStr = state || ""
	const zipStr = zip ? zip.toString() : ""

	// Build address string with proper comma separation
	let address = streetStr

	if (cityStr) {
		if (address) address += ", "
		address += cityStr
	}

	if (stateStr) {
		if (address) address += ", "
		address += stateStr
	}

	if (zipStr) {
		if (address) address += ", "
		address += zipStr
	}

	return address
}

export const formatDate = (field) => {
	// If field is empty or not a string, return null
	if (!field || typeof field !== "string") {
		return null
	}

	let month, day, year
	let hours = 0,
		minutes = 0,
		seconds = 0

	try {
		// Check if the input includes time
		if (field.includes(" ")) {
			// Parse date and time
			const [dateStr, timeStr] = field.split(" ")
			;[month, day, year] = dateStr.split("/").map((num) => parseInt(num, 10))

			// Parse time if provided
			if (timeStr) {
				const timeParts = timeStr.split(":")
				hours = parseInt(timeParts[0] || 0, 10)
				minutes = parseInt(timeParts[1] || 0, 10)
				seconds = parseInt(timeParts[2] || 0, 10)
			}
		} else {
			// Only date provided - now parsing as MM/DD/YYYY
			;[month, day, year] = field.split("/").map((num) => parseInt(num, 10))
		}

		// Handle 2-digit years (assuming 20xx for simplicity)
		if (year < 100) {
			year = 2000 + year
		}

		// Create date object (months are 0-indexed in JavaScript)
		const date = new Date(year, month - 1, day, hours, minutes, seconds)

		// Check if the date is valid
		if (isNaN(date.getTime())) {
			console.warn(`Invalid date format: ${field}`)
			return null
		}

		// Convert to Firebase Timestamp
		return Timestamp.fromDate(date)
	} catch (error) {
		console.error(`Error parsing date '${field}':`, error)
		return null
	}
}
/**
 * Submits a job request to the API
 * @param {Object} jobData - The job data to submit
 * @param {Object} userPlus - The user information
 * @returns {Promise<Object>} Result of the submission
 */
export const submitJobRequestToQuiplo = async (jobData, userPlus) => {
	try {
		// Return the formatted object
		const servicePreferredDates = {
			date: formatDate(jobData["Preferred Service Dates"]),
			startTime: "09:00",
			endTime: "17:00",
		}
		let user = null
		if (jobData["Work Order Contact Email"]) {
			const contactEmail = String(jobData["Work Order Contact Email"]).trim()
			const q = query(
				collection(db, "users"),
				where("email", "==", contactEmail)
			)
			const querySnapshot = await getDocs(q)
			user = querySnapshot.docs[0].data()
		}

		const jobRequestData = {
			id: crypto.randomUUID(),
			locationAddress: formatAddress(
				jobData.Address,
				jobData.City,
				jobData.State,
				jobData.Zip
			),
			isDealUnderContract: jobData["Is Deal Under Contract"],
			inspectionPeriod: jobData["Inspection Period Date"]
				? formatDate(jobData["Inspection Period Date"])
				: null,
			closingDate: jobData["Closing Date"]
				? formatDate(jobData["Closing Date"])
				: null,
			isElectricOn: jobData["Is Electric On?"],
			isWaterOn: jobData["Is Water On?"],
			isGasOn: jobData["Is Gas On?"],
			isPropertyOccupied: jobData["Is Property Occupied?"],
			serviceType: jobData["Service Type"],
			servicePreferredDates: [servicePreferredDates],
			jobRequestDetails: jobData["Job Request Details"],
			_createdAt: new Date(),
			_updatedAt: new Date(),
			_createdBy: userPlus.id,
			_updatedBy: userPlus.id,
			partner: userPlus.partner ? userPlus.partner : null,
			userId: user ? user.id : userPlus.id,
			userEmail: user ? user.email : userPlus.email,
			currentResidentName: jobData["Current Resident Name"],
			currentResidentPhone: jobData["Current Resident Phone Number"],
			currentResidentEmail: jobData["Current Resident Email"],
			clientEmailAddress: [jobData["Current Resident Email"]],
			vendorIds: ["591419e8-9359-4fe3-b98c-e5b8a91e6fd0"],
			teamId: user ? user.teamId : userPlus.teamId ? userPlus.teamId : null,
			lockCode: jobData["Lock Code Combo"],
			lockCodeLocation: jobData["Lock Code Location"],
			keyCode: jobData["Key Code"],
			gateCode: jobData["Gate Code"],
			files: [],
		}
		await addDoc(collection(db, "jobRequests"), jobRequestData)

		return {
			success: true,
			data: jobRequestData,
		}
	} catch (error) {
		return {
			success: false,
			error: error.message || "Failed to create job request in Quiplo",
		}
	}
}

/**
 * Submits a job request to the API
 * @param {Object} jobData - The job data to submit
 * @param {Object} userPlus - The user information
 * @returns {Promise<Object>} Result of the submission
 */
export const submitJobRequestToPPW = async (
	jobData,
	workOrderFields,
	userPlus,
	files = []
) => {
	try {
		// Wait for the Promise to resolve by using await
		await sendJobToPPW(jobData, userPlus, workOrderFields, files)

		// Return success response if no error was thrown
		return {
			success: true,
			data: jobData,
		}
	} catch (error) {
		console.error("Error submitting job to PPW:", error)

		return {
			success: false,
			error: error.message || "Failed to create job request in PPW",
		}
	}
}

/**
 * Processes a CSV file and submits job requests for each valid row
 * @param {File} file - The CSV file to process
 * @returns {Promise<Array>} Results of processing each row
 */
export const processCSV = async (file, userPlus) => {
	const workOrderFields = await axios.get(
		`${config.baseURL}/api/ppw-workorder-fields`
	)
	return new Promise((resolve, reject) => {
		Papa.parse(file, {
			header: true,
			skipEmptyLines: true,
			complete: async (results) => {
				try {
					// Skip the first row (instructions)
					const dataRows = results.data.slice(1)
					const processResults = []

					// Process each row
					for (let i = 0; i < dataRows.length; i++) {
						const row = dataRows[i]
						const processedRow = processCsvRow(row)
						const rowNumber = i + 2 // +2 because we skipped first row and arrays are 0-indexed

						// Validate row has all required fields
						const validation = validateRow(processedRow)

						if (!validation.valid) {
							processResults.push({
								rowNumber,
								status: "failed",
								address: processedRow.Address || "Unknown Address",
								city: processedRow.City || "",
								state: processedRow.State || "",
								zip: processedRow.Zip || "",
								serviceType: processedRow["Service Type"] || "Unknown Service",
								error:
									"Missing required fields: " +
									validation.missingFields.join(", "),
							})
							continue
						}

						// Submit job request
						const result = await submitJobRequestToQuiplo(
							processedRow,
							userPlus
						)

						if (result.success) {
							const PPWResult = await submitJobRequestToPPW(
								result.data,
								workOrderFields,
								userPlus
							)
							if (PPWResult.success) {
								processResults.push({
									rowNumber,
									status: "complete",
									address: processedRow.Address,
									city: processedRow.City,
									state: processedRow.State,
									zip: processedRow.Zip,
									serviceType: processedRow["Service Type"],
								})
							} else {
								processResults.push({
									rowNumber,
									status: "partial",
									address: processedRow.Address,
									city: processedRow.City,
									state: processedRow.State,
									zip: processedRow.Zip,
									serviceType: processedRow["Service Type"],
								})
							}
						} else {
							processResults.push({
								rowNumber,
								status: "failed",
								address: processedRow.Address,
								city: processedRow.City,
								state: processedRow.State,
								zip: processedRow.Zip,
								serviceType: processedRow["Service Type"],
								error: result.error,
							})
						}
					}

					axios.post(`${config.baseURL}/api/bulk-upload-notification`, {
						userEmail: userPlus.email,
					})

					resolve(processResults)
				} catch (error) {
					reject(error)
				}
			},
			error: (error) => {
				reject(error)
			},
		})
	})
}
